import { plural, t } from '@lingui/macro';

import Alert from '@decisiv/ui-components/lib/components/Alert';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3, P } from '@decisiv/ui-components/lib/components/Typography';

import { type PolledDepotReset } from '../hooks/usePollDepotReset';

interface ReviewStepProps {
  depotReset: PolledDepotReset;
}

// An account-wide reset can empty dozens of depots. The title already carries
// the magnitude; enumerating them all turns the one warning that must not be
// skimmed into a wall of semicolons.
const EMPTIED_DEPOT_LIST_CAP = 3;

export default function ReviewStep({ depotReset }: ReviewStepProps) {
  const emptied = depotReset.depotSummaries.filter((s) => s.willBeEmptied);
  const cappedEmptied = emptied.slice(0, EMPTIED_DEPOT_LIST_CAP);
  const emptiedOverflow = emptied.length - cappedEmptied.length;

  // One translatable sentence per emptied depot, joined; anything past the cap
  // collapses into its own "and N more" message.
  const emptiedDepotsSentence =
    cappedEmptied
      .map((s) => {
        // "will lose all 140 assets" with no depot named is worse than useless
        // on a destructive reset, and a nameless entry can't be cross-
        // referenced once the list is capped.
        const depot = s.depotName ?? s.depotId;
        // `plural` stays nested in the `t` template: hoisting it yields two
        // messages a translator can't make agree, and shipped "all 1 assets".
        return t`${depot} will lose all ${plural(s.currentAssetCount, {
          one: '# asset',
          other: '# assets',
        })}`;
      })
      .join('; ') +
    (emptiedOverflow > 0
      ? `, ${t`and ${plural(emptiedOverflow, {
          one: '# more depot',
          other: '# more depots',
        })}`}`
      : '');

  // The design's alert is informational for every plan. That renders "12 assets
  // added" and "this depot loses all 140 of its assets" with identical weight,
  // which is the whole risk this screen exists to catch — so an emptying plan
  // escalates to a warning that names the depots and their current size.
  const alertProps = emptied.length
    ? {
        intent: 'warning' as const,
        // The true total, not the capped enumeration below — capping this
        // would understate a destructive operation.
        title: plural(emptied.length, {
          one: '# depot will be emptied',
          other: '# depots will be emptied',
        }),
        description: t`${emptiedDepotsSentence}. Applying this reset releases every asset not listed in your CSV.`,
      }
    : {
        intent: 'information' as const,
        title: t`Review Reset Impact`,
        description: t`The following changes will be applied if you continue with the depot assets reset. Review the assets in each category before proceeding.`,
      };

  const { excludedDepots } = depotReset;
  const cappedExcluded = excludedDepots.slice(0, EMPTIED_DEPOT_LIST_CAP);
  const excludedOverflow = excludedDepots.length - cappedExcluded.length;

  const excludedDepotsSentence =
    cappedExcluded
      .map((d) => {
        // Same fallback, same reasoning as the emptied sentence above.
        const depot = d.depotName ?? d.depotId;
        // Null for MISSING_FMS_ID by API contract, so only the duplicate
        // branch interpolates it. Destructured so lingui names the placeholder
        // `{fmsId}` rather than a positional `{0}`.
        const { fmsId } = d;
        return d.reason === 'DUPLICATE_FMS_ID'
          ? t`${depot} shares FMS ID ${fmsId} with another depot`
          : t`${depot} has no FMS ID`;
      })
      .join('; ') +
    (excludedOverflow > 0
      ? `, ${t`and ${plural(excludedOverflow, {
          one: '# more depot',
          other: '# more depots',
        })}`}`
      : '');

  const lines = [
    {
      count: depotReset.addCount,
      text: t`Assets will be added`,
    },
    {
      count: depotReset.transferCount,
      text: t`Assets will be transferred between depots`,
    },
    { count: depotReset.updateCount, text: t`Assets will be updated` },
    {
      count: depotReset.releaseCount,
      text: t`Assets will be released from their current depot`,
    },
    // Only shown when non-zero: "0 rows could not be read" is noise, and this
    // line is the one that should draw the eye when it does appear.
    ...(depotReset.invalidCount > 0
      ? [
          {
            count: depotReset.invalidCount,
            text: t`Rows could not be read and will be skipped`,
          },
        ]
      : []),
  ];

  return (
    <Flex flexDirection="column">
      <H3>{t`Review`}</H3>

      <Flex marginTop={1.5} marginBottom={1.5}>
        <Alert {...alertProps} />
      </Flex>

      {excludedDepots.length > 0 && (
        <Flex marginBottom={1.5}>
          <Alert
            intent="warning"
            title={plural(excludedDepots.length, {
              one: '# depot was left out of this reset',
              other: '# depots were left out of this reset',
            })}
            description={t`${excludedDepotsSentence}. No assets will be released from these depots. Give each one a unique FMS ID and run the reset again to include it.`}
          />
        </Flex>
      )}

      {lines.map((line) => (
        <P key={line.text} size="small" shade={1}>
          {`${line.count} ${line.text}`}
        </P>
      ))}
    </Flex>
  );
}
